import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { api, type User } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { UserPlus, Mail, Trash2, Loader2, Link2, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export function UsersPage() {
  const { company } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planLimitError, setPlanLimitError] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formName, setFormName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [inviteLinkUserId, setInviteLinkUserId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    api.users.listByCompany(company.id).then(setUsers).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [company?.id]);

  const isMonitored = (u: User) => !!u.calendarWatchChannelId;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company?.id || !formEmail.trim()) return;
    setSubmitting(true);
    setError(null);
    setPlanLimitError(false);
    try {
      await api.users.create({
        email: formEmail.trim(),
        name: formName.trim() || undefined,
      });
      setFormEmail("");
      setFormName("");
      const list = await api.users.listByCompany(company.id);
      setUsers(list);
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.includes("Free プラン") || msg.includes("PLAN_LIMIT")) setPlanLimitError(true);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetInviteLink = async (userId: string) => {
    setInviteLinkUserId(userId);
    setError(null);
    try {
      const { inviteLink } = await api.users.getInviteLink(userId);
      await navigator.clipboard.writeText(inviteLink);
      setCopiedId(userId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setInviteLinkUserId(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!company?.id) return;
    setRemovingId(userId);
    setError(null);
    try {
      await api.users.delete(userId);
      const list = await api.users.listByCompany(company.id);
      setUsers(list);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">メンバー</h2>
        <p className="text-muted-foreground text-sm mt-1">営業担当のメールアドレスを追加・解除</p>
      </div>

      {company && (
        <>
          <Card className="border-border/80 bg-card/50 overflow-hidden relative rounded-xl shadow-sm">
            <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-primary/[0.03] to-transparent pointer-events-none" />
            <CardHeader className="relative">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <UserPlus className="size-4 text-primary/80" />
                メンバーを追加
              </CardTitle>
              <CardContent className="p-0 pt-4">
                <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-4 max-w-xl">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="email">メールアドレス</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="sales@example.com"
                      value={formEmail}
                      onChange={e => setFormEmail(e.target.value)}
                      className="rounded-lg"
                      required
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="name">名前（任意）</Label>
                    <Input
                      id="name"
                      placeholder="山田 太郎"
                      value={formName}
                      onChange={e => setFormName(e.target.value)}
                      className="rounded-lg"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" disabled={submitting} className="rounded-lg gap-2">
                      {submitting ? <Loader2 className="size-4 animate-spin" /> : <Mail className="size-4" />}
                      追加
                    </Button>
                  </div>
                </form>
              </CardContent>
            </CardHeader>
          </Card>

          {planLimitError && (
            <p className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              Free プランは 1 人までです。サイドバーから「Premium へ」でアップグレードしてください（500円/ユーザー/月）。
            </p>
          )}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">
              {company.name} の担当者一覧
            </h3>
            <div className="grid gap-3">
              <AnimatePresence mode="popLayout">
                {users.length === 0 ? (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-muted-foreground text-sm py-8 text-center"
                  >
                    担当者がいません。上から追加してください。
                  </motion.p>
                ) : (
                  users.map((user, i) => (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className={cn(
                        "rounded-xl border border-border/60 bg-card/50 p-4 flex items-center justify-between gap-4",
                        "hover:border-border transition-colors"
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{user.name || user.email}</p>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {user.googleLinked ? (
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 rounded-md">
                            連携済み
                          </span>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-xs"
                            onClick={() => handleGetInviteLink(user.id)}
                            disabled={inviteLinkUserId === user.id}
                            title="この担当者に送る Google 連携リンクをコピー"
                          >
                            {inviteLinkUserId === user.id ? (
                              <Loader2 className="size-3.5 animate-spin" />
                            ) : copiedId === user.id ? (
                              <Check className="size-3.5 text-emerald-600" />
                            ) : (
                              <Link2 className="size-3.5" />
                            )}
                            連携リンクを発行
                          </Button>
                        )}
                        {isMonitored(user) ? (
                          <span className="text-xs text-muted-foreground bg-muted/60 px-2 py-1 rounded-md">
                            フォロー中
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">未開始</span>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={removingId === user.id}
                          title="担当者を削除"
                        >
                          {removingId === user.id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Trash2 className="size-4" />
                          )}
                        </Button>
                      </div>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>
          </div>
        </>
      )}

      {error && !planLimitError && (
        <p className="text-destructive text-sm">{error}</p>
      )}

      {loading && !company && (
        <p className="text-muted-foreground text-sm">読み込み中…</p>
      )}
    </div>
  );
}
